<template>
  <div v-if="isPromotionActive" class="pricing-list-item">
    <div class="pricing-list-item__wrapper">
      <button
        @click="removeItemShoppingCart(product)"
        class="btn btn--small btn--tertiary btn--training pricing-list-item__btn"
      />
      <p class="pricing-list-item__selected-name">
        {{ product.nameToShow }}
      </p>
      <p class="pricing-list-item__selected-price">
        {{ `${currencySymbol}${productPrice}` }}
      </p>
    </div>
    <div v-if="!promoFundamentalsBundleOnCart" class="pricing-list-item__dropdowns-wrapper">
      <div class="pricing-list-item__dropdowns-input">
        <v-select
          :class="['ine-input-dropdown__wrapper', certError && 'ine-input--error']"
          :options="
            elegibleAvailableCertifications.map(cert => ({
              label: cert.name,
              code: cert.merchant_id,
            }))
          "
          :searchable="true"
          v-model="selectedCertification"
          placeholder="INE Security Certification"
        >
          <template #search="{ attributes, events }">
            <input
              readonly
              :required="!selectedCertification"
              :value="selectedCertification.label || selectedCertification"
              v-bind="attributes"
              v-on="events"
            />
          </template>
        </v-select>
        <span v-if="certError" class="ine-input__error-msg">Please choose a certification.</span>
      </div>
      <div v-if="promoBundleOneOnCart" class="pricing-list-item__dropdowns-input">
        <v-select
          :class="['ine-input-dropdown__wrapper', skillDiveLAError && 'ine-input--error']"
          :options="
            skillDivePlan.addOns.map(SDAddOn => ({
              label: SDAddOn.label,
              code: SDAddOn.merchant_id,
            }))
          "
          :searchable="true"
          v-model="selectedSDLA"
          placeholder="Skill Dive Skill Area"
        >
          <template #search="{ attributes, events }">
            <input
              readonly
              :required="!selectedSDLA"
              :value="selectedSDLA.label || selectedSDLA"
              v-bind="attributes"
              v-on="events"
            />
          </template>
        </v-select>
        <span v-if="skillDiveLAError" class="ine-input__error-msg">This field is required.</span>
      </div>
    </div>
    <div class="pricing-list-item__verbage">
      <div class="pricing-list-item__verbage-border"></div>
      <p class="pricing-list-item__verbage-text">
        {{ product.renewalMessage }}
      </p>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import vSelect from 'vue-select'
import { priceDetailUpdates, updateCart, promoProductBundleUtils } from '@/helpers/mixins'
import CONSTANTS from '@/constants'

const { BLACK_FRIDAY_PRODUCT_BUNDLES, LEAP_YEAR_PROMO_PRODUCT_BUNDLES } = CONSTANTS

const { ERRORS: BF_ERRORS } = BLACK_FRIDAY_PRODUCT_BUNDLES
const { ERRORS: LY_ERRORS } = LEAP_YEAR_PROMO_PRODUCT_BUNDLES

export default {
  name: 'PricingListItemProductBundle',
  mixins: [priceDetailUpdates, updateCart, promoProductBundleUtils],
  components: {
    vSelect,
  },
  props: {
    product: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      productBundleCert: 'INE Security Certification',
      productBundleSDLA: 'Skill Dive Skill Area',
      certError: false,
      skillDiveLAError: false,
    }
  },
  computed: {
    ...mapState({
      isPromotionActive: state => state.shoppingCart.isPromotionActive,
      bundleProductHasError: state => state.shoppingCart.bundleProductHasError,
    }),
    productPrice() {
      return this.product?.price_cents / 100
    },
    selectedCertification: {
      set(input) {
        this.productBundleCert = input
      },
      get() {
        return this.productBundleCert
      },
    },
    selectedSDLA: {
      set(input) {
        this.productBundleSDLA = input
      },
      get() {
        return this.productBundleSDLA
      },
    },
  },
  methods: {
    removeItemShoppingCart(item) {
      this.removeSelectedFromShoppingCart(item)
    },
  },
  created() {
    if (this.product.bundle) {
      this.productBundleCert = this.availableCertifications.find(
        cert => cert.merchant_id === this.product.bundle.voucher
      ).name
      if (this.promoBundleOneOnCart) {
        this.productBundleSDLA = this.skillDivePlan.addOns.find(
          SDLA => SDLA.merchant_id === this.product.bundle.skill_dive_area
        ).name
      }
    }
  },
  watch: {
    selectedCertification: {
      immediate: true,
      handler(selection) {
        if (selection.code) {
          this.certError = false
          this.setBundleProductError(false)
          const bundleObj = this.product.bundle || {}
          Object.assign(bundleObj, {
            slug: this.product.slug,
            voucher: selection.code,
          })
          this.product.bundle = bundleObj
        }
      },
    },
    selectedSDLA: {
      immediate: true,
      handler(selection) {
        if (selection.code) {
          this.skillDiveLAError = false
          this.setBundleProductError(false)
          const bundleObj = this.product.bundle || {}
          Object.assign(bundleObj, {
            slug: this.product.slug,
            skill_dive_area: selection.code,
          })
          this.product.bundle = bundleObj
        }
      },
    },
    bundleProductHasError: {
      immediate: true,
      handler(hasError) {
        if (hasError && hasError.type) {
          switch (hasError.type) {
            case BF_ERRORS.BUNDLE_ERROR_VOUCHER:
            case LY_ERRORS.BUNDLE_ERROR_VOUCHER:
              this.certError = true
              break
            case BF_ERRORS.BUNDLE_ERROR_SD:
              this.skillDiveLAError = true
              break
            default:
              this.certError = true
              this.skillDiveLAError = true
              break
          }
        }
      },
    },
  },
}
</script>

<style lang="scss" scoped>
.pricing-list-item {
  $this: &;
  border: 1px solid var(--tundora-gray);
  border-radius: 10px;
  padding: 15px;
  &__wrapper {
    display: grid;
    grid-template-columns: 40px auto 75px;
    align-items: flex-start;
    width: 100%;
    height: 30px;
    color: var(--white);
    font-size: rem(16);
    line-height: 23px;
  }
  &__btn {
    border: none;
    margin: 0;
    cursor: pointer;
    align-self: baseline;
    /* file name: '@/assets/icons/garbage_icon.svg' */
    background-image: url("data:image/svg+xml,%3Csvg width='20' height='22' viewBox='0 0 20 22' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M8 2C7.44772 2 7 2.44772 7 3V4H13V3C13 2.44772 12.5523 2 12 2H8ZM15 4V3C15 1.34315 13.6569 0 12 0H8C6.34315 0 5 1.34315 5 3V4H1C0.447715 4 0 4.44772 0 5C0 5.55228 0.447715 6 1 6H2V19C2 20.6569 3.34315 22 5 22H15C16.6569 22 18 20.6569 18 19V6H19C19.5523 6 20 5.55228 20 5C20 4.44772 19.5523 4 19 4H15ZM4 6V19C4 19.5523 4.44772 20 5 20H15C15.5523 20 16 19.5523 16 19V6H4ZM8 9C8.55228 9 9 9.44771 9 10V16C9 16.5523 8.55228 17 8 17C7.44772 17 7 16.5523 7 16V10C7 9.44771 7.44772 9 8 9ZM12 9C12.5523 9 13 9.44771 13 10V16C13 16.5523 12.5523 17 12 17C11.4477 17 11 16.5523 11 16V10C11 9.44771 11.4477 9 12 9Z' fill='white'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: center;
    background-size: 16px;
  }
  &__selected-name {
    margin: 0 auto 0 10px;
    text-align: left;
    width: 100%;
    @include mobile {
      margin: 0 auto 0 10px;
    }
  }
  &__selected-price {
    font-size: rem(18);
    line-height: 24px;
    text-align: right;
    margin: 0;
  }
  &__verbage {
    color: var(--dusty-gray);
    font-size: rem(12);
    line-height: 14px;
    text-align: left;
    margin: 0 0 15px 50px;
    display: flex;
    &-border {
      height: auto;
      width: 3px;
      border-left: 3px solid var(--white);
      border-radius: 10px;
    }
    &-text {
      margin: 0 0 0 10px;
    }
  }
  &__dropdowns {
    &-wrapper {
      display: flex;
      width: 100%;
      gap: 10px;
      padding: 20px 0 20px 50px;
      flex-flow: column;
      @include mobile {
        flex-flow: row;
      }
    }
    &-input {
      width: 100%;
      text-align: left;
    }
  }
}
</style>
